import React, { useEffect, useState } from 'react';
import { supabase } from '../../services/supabase';
import { usePeriodFilter } from '../../hooks/usePeriodFilter';
import { useEmpreendimentoFilter } from '../../hooks/useEmpreendimentoFilter';
import PeriodSelector from '../common/PeriodSelector';
import EmpreendimentoSelector from '../common/EmpreendimentoSelector';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import ChartContainer from '../common/ChartContainer';
import { applyDashboardFilters } from '../../utils/supabaseHelpers';
import { useAuth } from '../../contexts/AuthContext';

interface Lead {
    id: number;
    nomeCliente: string;
    telefoneCliente: string;
    corretor_responsavel: string;
    created_at: string;
    data_atribuicao_corretor: string | null;
    reagendar: boolean;
    usuario_reagendou: string | null;
    DataAtualizacao: string | null;
}

interface BrokerLeadsDetail {
    corretor: string;
    totalLeads: number;
    leads: Lead[];
    isExpanded: boolean;
}

const BrokerLeadsDetailChart: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState<number | null>(null);
    const { user } = useAuth();

    // ✅ Hook centralizado para gerenciar o filtro de período
    const periodFilter = usePeriodFilter();
    const empreendimentoFilter = useEmpreendimentoFilter();

    const [brokersData, setBrokersData] = useState<BrokerLeadsDetail[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // ✅ Utiliza função centralizada para aplicar filtro de data
            let query = supabase
                .from('Cadastro_Clientes')
                .select('id, nomeCliente, telefoneCliente, corretor_responsavel, created_at, data_atribuicao_corretor, reagendar, usuario_reagendou, DataAtualizacao')
                .not('corretor_responsavel', 'is', null)
                .order('created_at', { ascending: false });

            query = applyDashboardFilters(query, {
                dateRange: periodFilter.dateRange,
                empreendimento: empreendimentoFilter.selectedEmpreendimento,
                dateColumn: 'data_atribuicao_corretor',
            });

            const { data: leadsData, error } = await query;

            if (error) {
                console.error('❌ Error fetching data:', error);
                setError(error.message);
                return;
            }

            // ✅ Agrupar leads por corretor
            const grouped: Record<string, Lead[]> = {};
            
            leadsData?.forEach((lead: any) => {
                const corretor = lead.corretor_responsavel?.trim();
                
                if (!corretor || corretor === '') {
                    return;
                }
                
                if (!grouped[corretor]) {
                    grouped[corretor] = [];
                }
                
                grouped[corretor].push({
                    id: lead.id,
                    nomeCliente: lead.nomeCliente || 'Não informado',
                    telefoneCliente: lead.telefoneCliente || 'Não informado',
                    corretor_responsavel: corretor,
                    created_at: lead.created_at,
                    data_atribuicao_corretor: lead.data_atribuicao_corretor || null,
                    reagendar: lead.reagendar ?? false,
                    usuario_reagendou: lead.usuario_reagendou ?? null,
                    DataAtualizacao: lead.DataAtualizacao ?? null
                });
            });

            // Converter para array e ordenar por quantidade de leads
            const brokersArray: BrokerLeadsDetail[] = Object.entries(grouped)
                .map(([corretor, leads]) => ({
                    corretor,
                    totalLeads: leads.length,
                    leads,
                    isExpanded: false
                }))
                .sort((a, b) => b.totalLeads - a.totalLeads);

            setBrokersData(brokersArray);
        } catch (err: any) {
            console.error('❌ Erro ao processar dados:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchData();

        // Realtime subscription
        const channel = supabase
            .channel('broker-details-changes')
            .on('postgres_changes', 
                { event: '*', schema: 'public', table: 'Cadastro_Clientes' },
                () => {
                    void fetchData();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [periodFilter.dateRange, empreendimentoFilter.selectedEmpreendimento]);

    const toggleExpand = (index: number) => {
        setBrokersData(prev => prev.map((broker, i) =>
            i === index ? { ...broker, isExpanded: !broker.isExpanded } : broker
        ));
    };

    const toggleReagendar = async (leadId: number, currentStatus: boolean) => {
        try {
            setUpdating(leadId);
            const agora = new Date().toISOString();
            const novoStatus = !currentStatus;

            const { error } = await supabase
                .from('Cadastro_Clientes')
                .update({
                    reagendar: novoStatus,
                    usuario_reagendou: novoStatus ? user?.username ?? null : null,
                    DataAtualizacao: agora
                })
                .eq('id', leadId);

            if (error) {
                console.error('Erro ao atualizar reagendar:', error);
                alert('Erro ao atualizar status de reagendamento');
                return;
            }

            setBrokersData(prev => prev.map(broker => ({
                ...broker,
                leads: broker.leads.map(lead =>
                    lead.id === leadId ? {
                        ...lead,
                        reagendar: novoStatus,
                        usuario_reagendou: novoStatus ? user?.username ?? null : null,
                        DataAtualizacao: agora
                    } : lead
                )
            })));
        } catch (err) {
            console.error('Erro:', err);
            alert('Erro ao atualizar status');
        } finally {
            setUpdating(null);
        }
    };

    const filteredBrokers = brokersData.filter(broker =>
        broker.corretor.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ✅ Componente reutilizável para loading
    if (loading) {
        return <LoadingSpinner color="blue" />;
    }

    // ✅ Componente reutilizável para erro
    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <div className="w-full">
            {/* ✅ Componente reutilizável para título */}
            <ChartContainer title="Detalhamento de Leads por Corretor">
                {/* ✅ Componente reutilizável para seletor de período */}
                <PeriodSelector {...periodFilter} color="blue" />
                <EmpreendimentoSelector
                    selectedEmpreendimento={empreendimentoFilter.selectedEmpreendimento}
                    setSelectedEmpreendimento={empreendimentoFilter.setSelectedEmpreendimento}
                    empreendimentos={empreendimentoFilter.empreendimentos}
                    loading={empreendimentoFilter.isLoadingEmpreendimentos}
                    color="blue"
                />

                {empreendimentoFilter.empreendimentoError && (
                    <p className="mt-2 text-xs sm:text-sm text-red-600">
                        Erro ao carregar empreendimentos: {empreendimentoFilter.empreendimentoError}
                    </p>
                )}

                {/* Campo de busca */}
                <div className="mb-3 sm:mb-4">
                    <input
                        type="text"
                        placeholder="Buscar corretor..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </div>

                {/* Lista de corretores com altura responsiva */}
                <div className="space-y-3 max-h-72 sm:max-h-80 md:max-h-96 overflow-y-auto">
                    {filteredBrokers.length === 0 && (
                        <p className="text-sm text-gray-500 px-2 py-1">
                            Nenhum corretor encontrado para os filtros selecionados.
                        </p>
                    )}
                    {filteredBrokers.map((broker, index) => (
                        <div key={broker.corretor} className="border border-gray-300 rounded-lg overflow-hidden">
                            {/* Cabeçalho do corretor */}
                            <button
                                onClick={() => toggleExpand(index)}
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-blue-50 hover:bg-blue-100 transition-colors flex justify-between items-center"
                            >
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <span className="font-semibold text-gray-800 text-sm sm:text-base">{broker.corretor}</span>
                                    <span className="px-2 sm:px-3 py-1 bg-blue-600 text-white rounded-full text-xs sm:text-sm font-medium">
                                        {broker.totalLeads} {broker.totalLeads === 1 ? 'lead' : 'leads'}
                                    </span>
                                </div>
                                <svg
                                    className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform ${broker.isExpanded ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Lista de leads expandida */}
                            {broker.isExpanded && (
                                <div className="bg-white overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Cliente</th>
                                                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Telefone</th>
                                                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Data Entrada Lead</th>
                                                <th className="px-3 sm:px-4 py-2 text-left text-xs sm:text-sm font-semibold text-gray-700">Data Atribuição Corretor</th>
                                                <th className="px-3 sm:px-4 py-2 text-center text-xs sm:text-sm font-semibold text-gray-700">Reagendar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {broker.leads.map((lead) => (
                                                <tr key={lead.id} className="border-t border-gray-200 hover:bg-gray-50">
                                                    <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-800">{lead.nomeCliente}</td>
                                                    <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-600">{lead.telefoneCliente}</td>
                                                    <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-500">
                                                        {new Date(lead.created_at).toLocaleDateString('pt-BR', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td className="px-3 sm:px-4 py-2 text-xs sm:text-sm text-gray-500">
                                                        {lead.data_atribuicao_corretor
                                                            ? new Date(lead.data_atribuicao_corretor).toLocaleDateString('pt-BR', {
                                                                day: '2-digit',
                                                                month: '2-digit',
                                                                year: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })
                                                            : ''}
                                                    </td>
                                                    <td className="px-3 sm:px-4 py-2 text-center">
                                                        <button
                                                            onClick={() => toggleReagendar(lead.id, lead.reagendar)}
                                                            disabled={updating === lead.id}
                                                            className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-all cursor-pointer ${lead.reagendar ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'} ${updating === lead.id ? 'opacity-50 animate-pulse' : ''}`}
                                                            title={lead.reagendar
                                                                ? `Reagendado por ${lead.usuario_reagendou ?? 'desconhecido'} — clique para desmarcar`
                                                                : 'Marcar para reagendar'}
                                                        >
                                                            {updating === lead.id ? (
                                                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            ) : lead.reagendar ? (
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Totais */}
                <div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
                        <div>
                            <strong className="text-gray-700">Total de corretores:</strong>{' '}
                            <span className="text-gray-900">{filteredBrokers.length}</span>
                        </div>
                        <div>
                            <strong className="text-gray-700">Total de leads:</strong>{' '}
                            <span className="text-gray-900">
                                {filteredBrokers.reduce((sum, broker) => sum + broker.totalLeads, 0)}
                            </span>
                        </div>
                    </div>
                </div>
            </ChartContainer>
        </div>
    );
};

export default BrokerLeadsDetailChart;
